import { useCallback, useEffect, useMemo } from 'react';
import { AddInstance, ClearInstance } from 'app/area/case-report/case-listing/action-types';
import {
  selectCachedUiDefinition, selectCasePermissions, selectCaseResult,
  selectCaseResults, selectErrorsExecuteFailed, selectFetching,
  selectRenderMode, selectTotalNumberOfResults
} from 'app/area/case-report/case-listing/selectors';
import { PrimaryActionComponentFactory } from 'app/area/case-report/case-listing/types';
import { CaseResult } from 'app/area/case-report/case-listing/types-store';
import { useInstanceKey, useInstanceOptions } from 'app/area/favourite/favourite-context';
import { HeaderVisibilityConfiguration } from 'app/components';
import { debounceExecuteFavouriteWait, STATE } from 'app/constants';
import { SchemaBuilder } from 'app/infrastructure/ui/builder';
import { UiDefinition } from 'app/infrastructure/ui/types-uidefinition';
import { InitialUiEngineProps } from 'app/infrastructure/ui/types-uischema';
import { FavouriteDefinition, RenderMode } from 'app/types-shared';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreators as caseListingActionCreators } from './actions';
import * as navigate from 'app/area/shared/navigation';
import { useIntl } from 'react-intl';
import { selectSelectedReport } from 'app/area/favourite/selectors';
import { AppDispatch } from 'app/infrastructure/configure-store';

export function useInstanceSlice() {
  const dispatch: AppDispatch = useDispatch();
  const instanceKey = useInstanceKey();

  useEffect(
    () => {
      dispatch(new AddInstance(instanceKey));
      return () => {
        dispatch(new ClearInstance(instanceKey));
      };
    },
    [instanceKey]
  );
}

export function useCaseListingEvents(
  workingDefinition: FavouriteDefinition,
  primaryActionComponentFactory: PrimaryActionComponentFactory,
  requestedRenderMode: RenderMode,
  fetching: boolean,
  autoExecute: boolean,
  results?: CaseResult[],
  totalNumberOfResults?: number
) {
  const instanceKey = useInstanceKey();

  const {
    executionCounter, 
    // To override the API for the report in the dashboard
    reportApiOverrides
  } = useInstanceOptions();

  const {
    executeFavouriteDefinition,
    setRenderMode,
    clearResults
  } = useActionCreators();

  const debouncedExecute = useCallback(debounce((definition, startIndex) => {
    executeFavouriteDefinition({
      definition, loadPermissions: !!primaryActionComponentFactory, startIndex,
      replaceResult: startIndex === 0,
      overrideExecuteFavourite: reportApiOverrides?.executeFavourite
    });
  }, debounceExecuteFavouriteWait), [primaryActionComponentFactory, instanceKey]);

  useEffect(() => {
    setRenderMode(requestedRenderMode || RenderMode.report);
  }, [requestedRenderMode]);

  const execute = useCallback((clearPreviousResult: boolean) => {
    if (clearPreviousResult) {
      clearResults();
    }
    const startIndex = !clearPreviousResult && results ? results?.length : 0;
    debouncedExecute(workingDefinition, startIndex);
  }, [workingDefinition, results, debouncedExecute, instanceKey]);

  const fetchMoreData = useCallback(() => {
    const hasMoreData = !!workingDefinition
      && !fetching
      && results.length !== 0
      && results.length !== totalNumberOfResults;

    if (hasMoreData) {
      execute(false);
    }
  }, [
    execute, workingDefinition,
    fetching, results, totalNumberOfResults,
    instanceKey
  ]);

  useEffect(() => {
    autoExecute && execute(true);
    return () => {
      clearResults();
    };
  }, [autoExecute, workingDefinition, instanceKey, executionCounter]);

  return {
    fetchMoreData
  };
}

export function useActionCreators() {
  const dispatch = useDispatch();
  const instanceKey = useInstanceKey();
  return useMemo(() => bindActionCreators(caseListingActionCreators(instanceKey), dispatch), [instanceKey]);
}

export function useSelectors() {
  const instanceKey = useInstanceKey();
  const fetching = useSelector(selectFetching(instanceKey));
  const results = useSelector(selectCaseResults(instanceKey));
  const totalNumberOfResults = useSelector(selectTotalNumberOfResults(instanceKey));
  const errorsExecuteFailed = useSelector(selectErrorsExecuteFailed(instanceKey));
  const renderMode = useSelector(selectRenderMode(instanceKey));

  return {
    fetching,
    results,
    totalNumberOfResults,
    errorsExecuteFailed,
    renderMode
  };
}

export function useInstanceStatePath() {
  const instanceKey = useInstanceKey();
  return useMemo(() => [STATE.caseListing, 'instances', instanceKey], [instanceKey]);
}

export function useInitialUiEngineProps(
  renderMode: RenderMode,
  uiDefinition: UiDefinition,
  entityId: number,
  entityStoreKey: string,
  caseNumber: number,
  headerVisibilityConfiguration: HeaderVisibilityConfiguration
): InitialUiEngineProps {

  const intl = useIntl();

  const instanceKey = useInstanceKey();
  const instanceStatePath = useInstanceStatePath();

  const selectedReport = useSelector(selectSelectedReport(instanceKey));

  const {
    deleteCase,
    getPermissions
  } = useActionCreators();

  const schema = useMemo(
    () => uiDefinition && SchemaBuilder.create(renderMode, uiDefinition),
    [renderMode, uiDefinition]
  );

  const handleDeleteCase = useCallback(() => {
    deleteCase(caseNumber);
  }, [deleteCase, caseNumber, instanceKey]);

  const handleEditCase = useCallback(() => {
    navigate.toCaseEdit(caseNumber);
  }, [caseNumber]);

  const handleDuplicateCase = useCallback(() => {
    navigate.toCaseDuplicate(caseNumber);
  }, [caseNumber]);

  const handleBowtie = useCallback(() => {
    navigate.toBowtie(caseNumber);
  }, [caseNumber]);

  const handleLoadPermissions = useCallback(() => {
    getPermissions([caseNumber]);
  }, [getPermissions, caseNumber, instanceKey]);

  const buildProps = (): InitialUiEngineProps => ({
    reconnect: false,
    statePath: instanceStatePath,
    intl,
    entityId: entityId,
    entityStoreKey: entityStoreKey,
    caseNumber: caseNumber,
    applicationId: schema?.applicationId,
    componentDescriptor: schema?.children[0],
    actions: {
      editDocument: handleEditCase,
      duplicateDocument: handleDuplicateCase,
      deleteDocument: handleDeleteCase,
      loadBowtie: handleBowtie,
      loadPermissions: handleLoadPermissions,
      selectDocumentItem: undefined
    },
    headerVisibilityConfiguration,
    selectedReport: selectedReport.v
  });

  // eslint:disable-next-line: no-unnecessary-local-variable
  const props = useMemo(() => schema ? {
    ...buildProps(),
    hostProps: buildProps
  } : undefined,
    [intl, entityId, entityStoreKey, caseNumber,
      schema, headerVisibilityConfiguration, handleEditCase,
      handleDuplicateCase, handleDeleteCase, handleBowtie,
      handleLoadPermissions, instanceStatePath]
  );

  return props;
}

export function useSelectorsTile(caseNumber: number, entityId: number, entityStoreKey: string) {
  const instanceKey = useInstanceKey();
  const item = useSelector(selectCaseResult(instanceKey, caseNumber, entityId, entityStoreKey));
  const uiDefinition = useSelector(selectCachedUiDefinition(instanceKey, item?.caseTypeId, item?.caseContextId));
  const permissions = useSelector(selectCasePermissions(instanceKey, item?.caseNumber));

  return {
    item,
    uiDefinition,
    permissions
  };
}
